import React, { useState, useEffect } from 'react';
import expandIcon from '../assets/images/expand.svg'; // Adjust the path as necessary
import collapseIcon from '../assets/images/collapse.svg'; // Adjust the path as necessary

const Legend = ({ legendData, onOpacityChange, isTemporal, setShowAggregated, showAggregated }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [opacity, setOpacity] = useState(0.8); // Single opacity value for all colors

  const toggleContent = () => {
    setIsExpanded(!isExpanded);
  };

  // useEffect to reset opacity when legendData changes, if needed
  useEffect(() => {
    if (legendData) {
      setOpacity(0.8); // Reset to default opacity when legendData changes
    }
  }, [legendData]);

  const showLegend = () => {
    setIsExpanded(true);
  };

  const hideLegend = () => {
    setIsExpanded(false);
  };

  const singleHazardOpacityChange = (value) => {
    const newOpacity = parseFloat(value);
    setOpacity(newOpacity);
    // Notify the parent for each color
    legendData.forEach((item) => {
      onOpacityChange(item.color, newOpacity);
    });
  };

  // Generate gradient background dynamically from the data
  const gradientColors = legendData.map(item => item.color).join(", ");

  const gradientStyle = {
    width: "100%",
    height: "20px",
    borderRadius: "5px",
    background: `linear-gradient(to right, ${gradientColors})`,
  };

  const handleToggleAggregated = () => {
    setShowAggregated((prev) => !prev);
  };

  return (
    <>
      <div className="card legendCard border-0 legendFilter">
        <div className="card-header px-2 py-1 border-0 d-flex mb-0 align-items-center">
          <div><h3 className="legend-title mb-0">{isTemporal ? 'Occurrences' + '\u00A0' + '(1954-2020)' : 'Legends'}</h3></div>
          <div>
            <button onClick={toggleContent} className="toggle-button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <img
                src={isExpanded ? collapseIcon : expandIcon}
                alt={isExpanded ? 'Collapse' : 'Expand'}
                className="toggle-icon"
              />
            </button>
          </div>
        </div>
        {isExpanded && !isTemporal && (
          <div className="card-content p-2 pt-0">
            <div>
              <ul className="d-flex flex-wrap legend-text mb-0 pb-0" style={{ listStyleType: "none", padding: 0 }}>
                {legendData.map((item, index) => (
                  <li key={index} style={{ display: "flex", alignItems: "center", marginBottom: "5px", alignItems: "center" }}>
                    {/* Symbol */}
                    {item.color ? (
                      <span
                        style={{
                          display: "inline-block",
                          width: "32px",
                          height: "16px",
                          marginRight: "10px",
                          backgroundColor: item.color,
                        }}
                      ></span>
                    ) : (
                      <span
                        style={{
                          display: "inline-block",
                          width: "32px",
                          height: "16px",
                          marginRight: "10px",
                          border: "1px solid #ccc",
                        }}
                      ></span>
                    )}

                    {/* Symbol Text */}
                    <span style={{ width: "60px", fontSize: "11px" }}>{item.symbol ?? item.label}</span>
                  </li>
                ))}
              </ul>
              {legendData
                .filter(item => item.symbol && !item.symbol.includes('+'))
                .reduce((acc, item, index) => {
                  if (index % 2 === 0) {
                    acc.push([item]);
                  } else {
                    acc[acc.length - 1].push(item);
                  }
                  return acc;
                }, [])
                .map((row, rowIndex) => (
                  <div key={rowIndex} style={{ display: "flex", gap: "10px", marginBottom: "5px", marginLeft: '15px' }}>
                    {row.map((item, index) => (
                      <div key={index} style={{ fontSize: "12px", marginLeft: '-22px', color: "#555", fontWeight: "500", minWidth: "120px" }}>
                        <b className="ms-2">{item.symbol}</b> - {item.label}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        )}
        {isExpanded && isTemporal && (
          <div className="gradient-legend" style={{ padding: "10px" }}>
            {/* Discrete Color Blocks */}
            <div className="color-bar" style={{ display: "flex", width: "100%", flexWrap: "nowrap" }}>
              {legendData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: item.color,
                    flex: `1 0 ${100 / legendData.length}%`, // Equal width for each block
                    height: "10px",
                    border: "1px solid black",
                    borderLeft: index > 0 ? "none" : "1px solid black",
                  }}
                ></div>
              ))}
            </div>

            {/* Labels Below the Color Blocks */}
            <div
              className="gradient-labels"
              style={{
                display: "flex",
                width: "100%",
                flexWrap: "nowrap",
                marginTop: "5px",
              }}
            >
              {legendData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    flex: `1 0 ${100 / legendData.length}%`, // Match the width of color blocks
                    textAlign: "center", // Center the text
                    fontSize: "12px",
                    color: "black",
                  }}
                >
                  <span>{item.intensity}</span>
                </div>
              ))}
            </div>

            <div className="form-group" style={{ display: "flex", alignItems: "center" }}>
              <label className="legend-title mb-0" style={{ marginRight: "10px" }}>
                Aggregated&nbsp;Occurrences
              </label>
              <input
                className="form-check-input ms-2 mb-1"
                type="checkbox"
                checked={showAggregated}
                onChange={() => handleToggleAggregated()}
                style={{ marginTop: "6px" }}
              />
            </div>
          </div>
        )}

        <div style={{ margin: "0 0 10px 10px" }}>
          <input
            className="ml-2"
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={opacity}
            onChange={(e) => singleHazardOpacityChange(e.target.value)}
            style={{
              width: "100px",
              height: "5px",
              borderRadius: "10px",
              appearance: "none",
              background: `linear-gradient(to right, #105586 ${opacity * 100}%, #ccc 0%)`,
            }}
          />
          <span style={{ fontSize: "10px", fontWeight: "600", marginLeft: "10px" }}>
            {opacity === 1 || opacity === 0 ? opacity : opacity.toFixed(1)}
          </span>
        </div>
      </div>
    </>
  );
};

export default Legend;